import {
  useRef,
  useEffect,
  useState,
  InputHTMLAttributes,
  KeyboardEvent,
  FocusEvent,
} from 'react';

import { useTranslation } from 'react-i18next';

import styles from './NameInput.module.scss';
import { IconButton } from '@/components/ui/IconButton';

interface NameInputProps extends InputHTMLAttributes<HTMLInputElement> {
  onConfirm?: (name: string) => void;
  onCancel?: () => void;
  defaultName?: string;
  autoFocus?: boolean;
  isLoading?: boolean;
}

export const NameInput = ({
  value = '',
  onConfirm,
  onCancel,
  defaultName = '',
  autoFocus = false,
  isLoading = false,
  ...props
}: NameInputProps) => {
  const { t } = useTranslation('views');
  const inputRef = useRef<HTMLInputElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);
  const [inputValue, setInputValue] = useState<string>(String(value));

  useEffect(() => {
    if (spanRef.current && inputRef.current) {
      // Update the mirror span's text
      spanRef.current.textContent = inputValue || ' ';
      // Copy font styles
      const inputStyle = window.getComputedStyle(inputRef.current);
      spanRef.current.style.font = inputStyle.font;
      // Set input width = mirror's width (+ a little padding for caret)
      inputRef.current.style.width = `${spanRef.current.offsetWidth + 2}px`;
    }
  }, [inputValue]);

  // If parent passes a controlled value, update when it changes
  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  // Auto-focus input when autoFocus is true and select all text
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [autoFocus]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || (e.ctrlKey && e.key === 'Enter')) {
      e.preventDefault();
      e.stopPropagation();
      if (onConfirm && inputValue.trim()) {
        onConfirm(inputValue.trim());
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (onCancel) {
        setInputValue(defaultName);
        onCancel();
      }
    }

    // Call parent's onKeyDown if provided
    props.onKeyDown?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    // Just lose focus, keep the current value
    // Don't call onConfirm or onCancel
    props.onBlur?.(e);
  };

  const handleConfirmClick = () => {
    if (onConfirm && inputValue.trim()) {
      onConfirm(inputValue.trim());
    }
  };

  return (
    <div className={styles.NewItem}>
      <input
        type="text"
        className={styles.NewItemInput}
        ref={inputRef}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          props.onChange?.(e);
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isLoading}
        data-automation-id="view-name-input"
        {...props}
      />
      <span ref={spanRef} className={styles.NewItemMirror} aria-hidden="true" />
      <IconButton
        size="tiny"
        iconName={isLoading ? 'progress_activity' : 'check'}
        tooltip={isLoading ? t('Saving') : t('Confirm')}
        className={styles.NewNameAction}
        onClick={handleConfirmClick}
        disabled={!inputValue.trim() || isLoading}
        data-automation-id="view-name-confirm-button"
      />
    </div>
  );
};
